import React, { useEffect, useRef, useState } from 'react';
import ForeFlightHeader from './ForeFlightHeader';
import {
    CenteredContainer,
    Link,
    LoginWrap,
    StyledCheckbox,
    StyledTextField,
} from './styledComponents';
import Typography from '@mui/material/Typography';
import Alerts, { AlertType, TowerAlert } from '../../common/Alerts';
import FormHandler from './FormHandler';
import PasswordField from './PasswordField';
import { ControlBox } from './FormController';
import Footer from './Footer';
import { Checkbox, Stack } from '@mui/material';
import isEmpty from 'lodash/isEmpty';
import useCreateAccount from '../slices/hooks/useCreateAccount';
import useFocus from './hooks/useFocus';

const consentText =
    'I agree to allow ForeFlight to store my contact information and send me ' +
    'communications, including emails, regarding product updates, instructions, invitation ' +
    'to events, special offers, and holiday greetings.';

const CreateAccount = () => {
    const [username, setUsername] = useState<string>('');
    const [password, setPassword] = useState<string>('');
    const [password2, setPassword2] = useState<string>('');
    const [doesConsentToMarketing, setDoesConsentToMarketing] = useState<boolean>(true);
    const [alerts, setAlerts] = useState<TowerAlert[]>([]);
    const focusInputRef = useFocus();

    const {
        createAccount,
        isLoading,
        alerts: createAccountAlerts,
        isEuCustomer,
    } = useCreateAccount();

    const validateInputs = () => {
        const errors: TowerAlert[] = [];
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        if (isEmpty(username)) {
            errors.push({ type: AlertType.error, message: 'Username is required.' });
        } else if (!emailRegex.test(username)) {
            errors.push({
                type: AlertType.error,
                message: 'Username must be a valid email address.',
            });
        }

        if (isEmpty(password) || isEmpty(password2)) {
            errors.push({ type: AlertType.error, message: 'Both passwords are required.' });
        } else if (password !== password2) {
            errors.push({ type: AlertType.error, message: 'Passwords must match.' });
        }

        setAlerts(errors);
        return errors.length === 0;
    };

    const handleFormSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const isValid = validateInputs();
        if (isValid) {
            createAccount(username, password, doesConsentToMarketing);
        }
    };

    const onReset = () => {
        setUsername('');
        setPassword('');
        setPassword2('');
    };

    return (
        <LoginWrap>
            <CenteredContainer>
                <ForeFlightHeader showWelcomeHeader={false} onReset={() => {}} />
                <Alerts alerts={[...alerts, ...createAccountAlerts]} />
                <Typography variant="h5" mb="25px" gutterBottom style={{ textAlign: 'center' }}>
                    Create an account
                </Typography>
                <FormHandler
                    onSubmit={handleFormSubmit}
                    buttonText="Next"
                    isDisabled={isLoading}
                    isLoading={isLoading}
                >
                    <StyledTextField
                        fullWidth
                        label="Email"
                        placeholder="Required"
                        variant="standard"
                        margin="normal"
                        value={username}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            setUsername(e.target.value)
                        }
                        inputRef={focusInputRef}
                    />
                    <PasswordField
                        key={'password-visible'}
                        value={password}
                        placeholder="Required"
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            setPassword(e.target.value)
                        }
                    />
                    <PasswordField
                        key={'password-visible2'}
                        value={password2}
                        placeholder="Required"
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            setPassword2(e.target.value)
                        }
                    />
                </FormHandler>
                <ControlBox isCompact={false}>
                    <Stack
                        direction="column"
                        spacing={2}
                        mt="10px"
                        sx={{
                            justifyContent: 'flex-start',
                            alignItems: 'center',
                        }}
                    >
                        {isEuCustomer && (
                            <div style={{ width: '100%', textAlign: 'left' }}>
                                <StyledCheckbox
                                    label={consentText}
                                    control={
                                        <Checkbox
                                            size="small"
                                            checked={doesConsentToMarketing}
                                            onChange={e =>
                                                setDoesConsentToMarketing(e.target.checked)
                                            }
                                        />
                                    }
                                />
                            </div>
                        )}
                        <Typography color="gray" fontSize="12px" mb="10px">
                            Already have a ForeFlight ID?
                        </Typography>
                        <Link to="/login">Log In</Link>
                    </Stack>
                </ControlBox>
                <Footer />
            </CenteredContainer>
        </LoginWrap>
    );
};
export default CreateAccount;
